import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import PlayerAvatar from './ui/PlayerAvatar';
import { IconeLance, contarLances } from './Lances';
import { mediaDoTime } from './NotaJogador';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import tokens from '../theme/tokens';

const hora = (data) => new Date(data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
const dia = (data) => new Date(data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });

// Placar pelos gols lançados pelo árbitro
function contarPlacar(eventos = []) {
  const placar = [0, 0];
  eventos.forEach((e) => {
    if (e.tipo === 'gol' && (e.time === 0 || e.time === 1)) placar[e.time] += 1;
  });
  return { placar, temGols: eventos.some((e) => e.tipo === 'gol') };
}

// Lances do jogador na partida (gols, assistências e cartões), compactos para caber na coluna do celular
const LancesDoJogador = ({ contagem }) => {
  const itens = [
    contagem.gols > 0 && { tipo: 'gol', qtd: contagem.gols, nome: 'gol' },
    contagem.assistencias > 0 && { tipo: 'assistencia', qtd: contagem.assistencias, nome: 'assistência' },
    ...contagem.cartoes.map((tipo) => ({ tipo, qtd: 1, nome: `cartão ${tipo}` })),
  ].filter(Boolean);
  if (itens.length === 0) return null;
  const descricao = itens.map((i) => `${i.qtd} ${i.nome}${i.qtd > 1 ? 's' : ''}`).join(', ');
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }} aria-label={descricao} title={descricao}>
      {itens.map((item, i) => (
        <Stack key={i} direction="row" alignItems="center" spacing={0.25} sx={{ '& svg': { fontSize: '15px !important' } }}>
          <IconeLance tipo={item.tipo} />
          {item.qtd > 1 && <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1 }}>{item.qtd}</Typography>}
        </Stack>
      ))}
    </Stack>
  );
};

/** Uma coluna (time) do cartão: faixa com o nome do time e os jogadores com foto. */
const ColunaTime = ({ titulo, cor, jogadores = [], venceu, indice, eventos, forca }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      borderRadius: 2,
      overflow: 'hidden',
      border: 2,
      borderColor: venceu ? tokens.color.gold : 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5, bgcolor: cor, color: '#fff' }}>
      <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '1rem' }}>{titulo}</Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {forca !== null && (
          <Tooltip title="Força do time: média das notas dos jogadores (avaliações da pelada, ranking do torneio ou pote informado)" enterTouchDelay={0}>
            <Box component="span" sx={{ px: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.18)', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Força {forca}
            </Box>
          </Tooltip>
        )}
        {venceu && <EmojiEventsRoundedIcon sx={{ fontSize: 18, color: tokens.color.gold }} aria-label="Venceu" />}
      </Stack>
    </Stack>
    <Stack spacing={0.75} sx={{ p: 1 }}>
      {jogadores.map((jogador) => (
        <Stack key={jogador.user_name} direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <PlayerAvatar userId={jogador.guest ? null : jogador.user} name={jogador.user_name} size={30} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: { xs: '0.78rem', sm: '0.875rem' } }}>
              {jogador.user_name}
            </Typography>
            <LancesDoJogador contagem={contarLances(eventos, indice, jogador.user_name)} />
          </Box>
        </Stack>
      ))}
    </Stack>
  </Box>
);

/**
 * Partida no histórico (tela de Ranking): Time 1 ao lado do Time 2, cada jogador com foto e nome — cabe lado a
 * lado no celular. Destaca o vencedor (borda dourada e troféu) e, quando o árbitro lançou, o placar e os lances de
 * cada jogador (gols, assistências e cartões). "Força" de cada time = média das notas 0–100 dos jogadores
 * (as mesmas da formação dos times; a API manda em `partida.notas`).
 */
// "3 gols, 1 assistência e 1 cartão" — o que sai junto com a partida
function resumoDosLances(eventos) {
  const n = (tipos) => eventos.filter((e) => tipos.includes(e.tipo)).length;
  const partes = [
    [n(['gol']), 'gol', 'gols'],
    [n(['assistencia']), 'assistência', 'assistências'],
    [n(['amarelo', 'azul', 'vermelho']), 'cartão', 'cartões'],
  ]
    .filter(([q]) => q > 0)
    .map(([q, um, varios]) => `${q} ${q === 1 ? um : varios}`);
  if (partes.length === 0) return null;
  return partes.length === 1 ? partes[0] : `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`;
}

/** `onExcluir(partida)` (diretoria): mostra a lixeira e pede confirmação antes de chamar. */
const PartidaHistoricoCard = ({ partida, numero, onExcluir }) => {
  const eventos = partida.eventos || [];
  const { placar, temGols } = contarPlacar(eventos);
  const empate = partida.vencedor === -1;
  const [confirmar, setConfirmar] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const lances = resumoDosLances(eventos);
  const titulo = `${numero ? `Partida ${numero} · ` : ''}${dia(partida.dataPartida)} · ${hora(partida.dataPartida)}`;

  const excluir = async () => {
    setExcluindo(true);
    try {
      await onExcluir(partida);
      setConfirmar(false);
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1, px: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>{titulo}</Typography>
          {onExcluir && (
            <Tooltip title="Excluir partida">
              <IconButton size="small" onClick={() => setConfirmar(true)} aria-label={`Excluir ${titulo}`} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {empate ? (
          <Chip size="small" label={temGols ? `Empate · ${placar[0]} × ${placar[1]}` : 'Empate'} variant="outlined" />
        ) : (
          <Chip
            size="small"
            icon={<EmojiEventsRoundedIcon sx={{ color: `${tokens.color.gold} !important` }} />}
            label={`Time ${partida.vencedor === 0 ? 1 : 2} venceu${temGols ? ` · ${placar[0]} × ${placar[1]}` : ''}`}
            sx={{ bgcolor: partida.vencedor === 0 ? tokens.color.team1 : tokens.color.team2, color: '#fff', fontWeight: 700 }}
          />
        )}
      </Stack>
      <Stack direction="row" spacing={{ xs: 0.75, sm: 1.5 }} alignItems="stretch">
        <ColunaTime titulo="Time 1" cor={tokens.color.team1} jogadores={partida.times[0]} venceu={partida.vencedor === 0} indice={0} eventos={eventos} forca={mediaDoTime(partida.times[0], partida.notas)} />
        <ColunaTime titulo="Time 2" cor={tokens.color.team2} jogadores={partida.times[1]} venceu={partida.vencedor === 1} indice={1} eventos={eventos} forca={mediaDoTime(partida.times[1], partida.notas)} />
      </Stack>
      {onExcluir && (
        <Dialog open={confirmar} onClose={excluindo ? undefined : () => setConfirmar(false)} fullWidth maxWidth="xs">
          <DialogTitle>Excluir esta partida?</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <b>{titulo}</b> — {empate ? 'Empate' : `Time ${partida.vencedor === 0 ? 1 : 2} venceu`}{temGols ? ` (${placar[0]} × ${placar[1]})` : ''}.
              <br />
              {lances ? <>Também serão excluídos os lances desta partida: <b>{lances}</b>.</> : 'Nenhum gol, assistência ou cartão foi lançado nela.'}
              <br />
              A partida some do histórico e das estatísticas. Não dá para desfazer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmar(false)} disabled={excluindo}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={excluir} disabled={excluindo}>{excluindo ? 'Excluindo...' : 'Excluir partida'}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default PartidaHistoricoCard;
